import React, { useState, useEffect, useMemo, useRef } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown, Check, Search } from 'lucide-react';

export interface CountryPhoneConfig {
  code: string;        // e.g. 'VN'
  name: string;        // e.g. 'Việt Nam'
  dialCode: string;    // e.g. '+84'
  flag: string;        // e.g. '🇻🇳'
  minLength: number;
  maxLength: number;
  exactLength?: number;
  validLengths?: number[]; // e.g. [10, 11] for mobile + landline
  example: string;     // e.g. '0912345678'
}

export function isPhoneDigitsValid(country: CountryPhoneConfig, count: number): boolean {
  if (country.validLengths && country.validLengths.length > 0) {
    return country.validLengths.includes(count);
  }
  if (country.exactLength) {
    return count === country.exactLength;
  }
  return count >= country.minLength && count <= country.maxLength;
}

export const COUNTRIES: CountryPhoneConfig[] = [
  {
    code: 'VN',
    name: 'Việt Nam',
    dialCode: '+84',
    flag: '🇻🇳',
    minLength: 10,
    maxLength: 11,
    validLengths: [10, 11],
    example: '0912345678 hoặc 02838234567',
  },
  { code: 'US', name: 'Hoa Kỳ (United States)', dialCode: '+1', flag: '🇺🇸', minLength: 10, maxLength: 10, exactLength: 10, example: '2025550123' },
  { code: 'JP', name: 'Nhật Bản (Japan)', dialCode: '+81', flag: '🇯🇵', minLength: 10, maxLength: 11, example: '9012345678' },
  { code: 'KR', name: 'Hàn Quốc (South Korea)', dialCode: '+82', flag: '🇰🇷', minLength: 9, maxLength: 10, example: '1012345678' },
  { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬', minLength: 8, maxLength: 8, exactLength: 8, example: '81234567' },
  { code: 'GB', name: 'Vương quốc Anh (UK)', dialCode: '+44', flag: '🇬🇧', minLength: 10, maxLength: 10, exactLength: 10, example: '7911123456' },
  { code: 'CN', name: 'Trung Quốc (China)', dialCode: '+86', flag: '🇨🇳', minLength: 11, maxLength: 11, exactLength: 11, example: '13800138000' },
  { code: 'AU', name: 'Úc (Australia)', dialCode: '+61', flag: '🇦🇺', minLength: 9, maxLength: 9, exactLength: 9, example: '412345678' },
  { code: 'DE', name: 'Đức (Germany)', dialCode: '+49', flag: '🇩🇪', minLength: 10, maxLength: 11, example: '15123456789' },
  { code: 'FR', name: 'Pháp (France)', dialCode: '+33', flag: '🇫🇷', minLength: 9, maxLength: 9, exactLength: 9, example: '612345678' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦', minLength: 10, maxLength: 10, exactLength: 10, example: '4165550123' },
  { code: 'TH', name: 'Thái Lan (Thailand)', dialCode: '+66', flag: '🇹🇭', minLength: 9, maxLength: 10, example: '812345678' },
  { code: 'ID', name: 'Indonesia', dialCode: '+62', flag: '🇮🇩', minLength: 9, maxLength: 12, example: '8123456789' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', flag: '🇲🇾', minLength: 9, maxLength: 10, example: '123456789' },
  { code: 'PH', name: 'Philippines', dialCode: '+63', flag: '🇵🇭', minLength: 10, maxLength: 10, exactLength: 10, example: '9171234567' },
  { code: 'IN', name: 'Ấn Độ (India)', dialCode: '+91', flag: '🇮🇳', minLength: 10, maxLength: 10, exactLength: 10, example: '9876543210' },
  { code: 'RU', name: 'Nga (Russia)', dialCode: '+7', flag: '🇷🇺', minLength: 10, maxLength: 10, exactLength: 10, example: '9123456789' },
  { code: 'TW', name: 'Đài Loan (Taiwan)', dialCode: '+886', flag: '🇹🇼', minLength: 9, maxLength: 9, exactLength: 9, example: '912345678' }
];

export interface PhoneInputProps {
  value?: string;
  onChange?: (value: string, country: CountryPhoneConfig, isValid: boolean) => void;
  defaultCountryCode?: string; // e.g. 'VN' or '+84'
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  name?: string;
  showDigitCounter?: boolean;
}

export function PhoneInput({
  value = '',
  onChange,
  defaultCountryCode = 'VN',
  label,
  error: externalError,
  required = false,
  disabled = false,
  placeholder,
  className,
  name,
  showDigitCounter = true,
}: PhoneInputProps) {
  // Determine initial country
  const initialCountry = useMemo(() => {
    const found = COUNTRIES.find(
      (c) => c.code.toLowerCase() === defaultCountryCode.toLowerCase() || c.dialCode === defaultCountryCode
    );
    return found || COUNTRIES[0];
  }, [defaultCountryCode]);

  const [selectedCountry, setSelectedCountry] = useState<CountryPhoneConfig>(initialCountry);
  const [phoneNumber, setPhoneNumber] = useState<string>(value);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isTouched, setIsTouched] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Synchronize internal state with external value changes
  useEffect(() => {
    setPhoneNumber(value);
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Filtered country list
  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) return COUNTRIES;
    const q = searchQuery.toLowerCase();
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dialCode.includes(q)
    );
  }, [searchQuery]);

  // Digits count
  const rawDigits = phoneNumber.replace(/\D/g, '');
  const digitsCount = rawDigits.length;

  // Validation message
  const validationError = useMemo(() => {
    if (!isTouched && !phoneNumber) return null;
    if (required && digitsCount === 0) {
      return 'Vui lòng nhập số điện thoại';
    }
    if (digitsCount > 0) {
      if (selectedCountry.validLengths && selectedCountry.validLengths.length > 0) {
        if (!selectedCountry.validLengths.includes(digitsCount)) {
          if (selectedCountry.code === 'VN') {
            return `Số điện thoại Việt Nam (${selectedCountry.dialCode}) phải gồm 10 số (di động) hoặc 11 số (điện thoại bàn). Hiện tại: ${digitsCount} số`;
          }
          return `Số điện thoại ${selectedCountry.name} (${selectedCountry.dialCode}) phải gồm ${selectedCountry.validLengths.join(' hoặc ')} chữ số (Hiện tại: ${digitsCount})`;
        }
      } else if (selectedCountry.exactLength) {
        if (digitsCount !== selectedCountry.exactLength) {
          return `Số điện thoại ${selectedCountry.name} (${selectedCountry.dialCode}) phải nhập đúng ${selectedCountry.exactLength} chữ số (Hiện tại: ${digitsCount})`;
        }
      } else {
        if (digitsCount < selectedCountry.minLength || digitsCount > selectedCountry.maxLength) {
          return `Số điện thoại ${selectedCountry.name} (${selectedCountry.dialCode}) phải từ ${selectedCountry.minLength} đến ${selectedCountry.maxLength} chữ số (Hiện tại: ${digitsCount})`;
        }
      }
    }
    return null;
  }, [digitsCount, selectedCountry, required, isTouched, phoneNumber]);

  const isValid = !validationError && (digitsCount > 0 || !required);
  const activeError = externalError || (isTouched ? validationError : null);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;
    // Allow numbers, spaces, dashes, parentheses
    const sanitized = inputVal.replace(/[^\d\s\-\(\)]/g, '');
    setPhoneNumber(sanitized);
    setIsTouched(true);

    const cleanDigits = sanitized.replace(/\D/g, '');
    const checkValid = isPhoneDigitsValid(selectedCountry, cleanDigits.length);

    if (onChange) {
      onChange(sanitized, selectedCountry, checkValid);
    }
  };

  const handleSelectCountry = (country: CountryPhoneConfig) => {
    setSelectedCountry(country);
    setIsOpen(false);

    const checkValid = isPhoneDigitsValid(country, rawDigits.length);

    if (onChange) {
      onChange(phoneNumber, country, checkValid);
    }
  };

  return (
    <div className={cn('space-y-1.5 w-full text-left', className)}>
      {label && (
        <label className="block text-xs font-bold text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative flex items-center" ref={dropdownRef}>
        {/* Country Code Selector Trigger */}
        <div className="relative">
          <button
            type="button"
            disabled={disabled}
            onClick={() => setIsOpen(!isOpen)}
            className={cn(
              'flex items-center gap-1.5 h-11 px-3 bg-gray-50 border border-r-0 border-gray-200 rounded-l-xl text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none shrink-0',
              disabled && 'opacity-50 cursor-not-allowed',
              activeError && 'border-red-500'
            )}
          >
            <span className="text-base leading-none">{selectedCountry.flag}</span>
            <span className="text-xs font-semibold text-gray-800">{selectedCountry.dialCode}</span>
            <ChevronDown size={14} className={cn('text-gray-400 transition-transform duration-200', isOpen && 'rotate-180')} />
          </button>

          {/* Country Dropdown Menu */}
          {isOpen && (
            <div className="absolute left-0 top-full mt-1.5 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
              {/* Search input */}
              <div className="p-2 border-b border-gray-100 bg-gray-50/80 sticky top-0">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Tìm quốc gia hoặc mã..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 text-xs bg-white rounded-lg border border-gray-200 outline-none focus:border-orange-500"
                  />
                </div>
              </div>

              {/* Country List */}
              <div className="max-h-60 overflow-y-auto divide-y divide-gray-50">
                {filteredCountries.length === 0 ? (
                  <div className="p-4 text-center text-xs text-gray-400">Không tìm thấy quốc gia</div>
                ) : (
                  filteredCountries.map((c) => {
                    const isSelected = c.code === selectedCountry.code;
                    return (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => handleSelectCountry(c)}
                        className={cn(
                          'w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-orange-50/60 transition-colors text-left',
                          isSelected && 'bg-orange-50 text-orange-600 font-semibold'
                        )}
                      >
                        <div className="flex items-center gap-2.5">
                          <span className="text-lg leading-none">{c.flag}</span>
                          <span className="text-gray-700">{c.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-gray-400 font-semibold">{c.dialCode}</span>
                          {isSelected && <Check size={14} className="text-orange-500 shrink-0" />}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Phone Input Box */}
        <div className="relative flex-1">
          <input
            name={name}
            type="tel"
            disabled={disabled}
            value={phoneNumber}
            onChange={handlePhoneChange}
            onBlur={() => setIsTouched(true)}
            placeholder={placeholder || `VD: ${selectedCountry.example}`}
            className={cn(
              'w-full h-11 px-4 text-sm bg-white border border-gray-200 rounded-r-xl outline-none transition-all',
              'focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10',
              disabled && 'bg-gray-50 cursor-not-allowed opacity-50',
              activeError && 'border-red-500 focus:border-red-500 focus:ring-red-500/10',
              showDigitCounter && 'pr-16'
            )}
          />

          {/* Digit Counter Badge */}
          {showDigitCounter && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <span
                className={cn(
                  'text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-md transition-colors',
                  digitsCount === 0 && 'bg-gray-100 text-gray-400',
                  digitsCount > 0 && isPhoneDigitsValid(selectedCountry, digitsCount) && 'bg-green-100 text-green-700',
                  digitsCount > 0 && !isPhoneDigitsValid(selectedCountry, digitsCount) && 'bg-amber-100 text-amber-700'
                )}
              >
                {digitsCount}
                {selectedCountry.validLengths
                  ? `/${selectedCountry.validLengths.join('-')}`
                  : selectedCountry.exactLength
                  ? `/${selectedCountry.exactLength}`
                  : ` (${selectedCountry.minLength}-${selectedCountry.maxLength})`}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Helper text or Error message */}
      {activeError ? (
        <p className="text-xs text-red-500 font-medium mt-1 animate-fade-in">
          {activeError}
        </p>
      ) : (
        <p className="text-[11px] text-gray-400 flex items-center justify-between">
          <span>
            Yêu cầu {selectedCountry.name}:{' '}
            {selectedCountry.code === 'VN'
              ? '10 số (di động) hoặc 11 số (bàn)'
              : selectedCountry.validLengths
              ? `${selectedCountry.validLengths.join(' hoặc ')} chữ số`
              : selectedCountry.exactLength
              ? `đúng ${selectedCountry.exactLength} chữ số`
              : `từ ${selectedCountry.minLength} đến ${selectedCountry.maxLength} chữ số`}
          </span>
          <span>VD: {selectedCountry.dialCode} {selectedCountry.example}</span>
        </p>
      )}
    </div>
  );
}

export default PhoneInput;
